import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, Box,
  Card, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import BuildIcon from '@mui/icons-material/Build';
import HistoryIcon from '@mui/icons-material/History';
import { motion } from 'framer-motion';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useApi } from '../api';

const OrdenesDashboard = () => {
  const [ordenesPendientes, setOrdenesPendientes] = useState([]);
  const [ordenesEnProceso, setOrdenesEnProceso] = useState([]);
  const [ordenesCerradas, setOrdenesCerradas] = useState([]);
  const [cerradasLoading, setCerradasLoading] = useState(false);
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [ordenAEliminar, setOrdenAEliminar] = useState(null);
  const api = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Cargar pendientes y en proceso por separado, solo una vez
  useEffect(() => {
    cargarPendientes();
    cargarEnProceso();
  }, []);

  const cargarPendientes = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/ordenes/?estado=pendiente` );
      setOrdenesPendientes(res.data);
    } catch (error) {
      console.error('Error al cargar órdenes pendientes', error);
    }
  };

  const cargarEnProceso = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/ordenes/?estado=en_proceso`);
      setOrdenesEnProceso(res.data);
    } catch (error) {
      console.error('Error al cargar órdenes en proceso', error);
    }
  };

  // Buscar órdenes cerradas por rango de fechas (historial)
  const buscarCerradas = async () => {
    if (!filtroDesde || !filtroHasta) {
      alert('Por favor selecciona ambas fechas');
      return;
    }
    setCerradasLoading(true);
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/api/ordenes/?estado=cerrada&fecha_recepcion__gte=${filtroDesde}&fecha_recepcion__lte=${filtroHasta}`
      );
      setOrdenesCerradas(res.data);
    } catch (error) {
      console.error('Error al cargar órdenes cerradas', error);
      setOrdenesCerradas([]);
    } finally {
      setCerradasLoading(false);
    }
  };

  const renderEstado = (estado) => {
    const color = {
      pendiente: 'warning',
      en_proceso: 'info',
      completado: 'success',
      cerrada: 'default'
    }[estado] || 'default';
    return <Chip label={estado.replace('_', ' ')} color={color} />;
  };

  const confirmarEliminacion = (orden) => {
    setOrdenAEliminar(orden);
    setOpenDialog(true);
  };

  const cancelarEliminacion = () => {
    setOrdenAEliminar(null);
    setOpenDialog(false);
  };

  const eliminarOrden = async () => {
    if (!ordenAEliminar) return;
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/api/ordenes/${ordenAEliminar.id}/`);
      setOrdenAEliminar(null);
      setOpenDialog(false);
      cargarPendientes();
      cargarEnProceso();
    } catch (error) {
      console.error('❌ Error al eliminar la orden:', error);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Título principal animado */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Typography className="titulo-app" variant="h5" gutterBottom>
          <AssignmentIcon sx={{ mr: 1, fontSize: 32, color: '#083a94', verticalAlign: 'middle' }} />
          Órdenes de Servicio
        </Typography>
      </motion.div>

      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => navigate('/ordenes/nueva')}
        sx={{ mb: 3 }}
      >
        Nueva Orden de Servicio
      </Button>

      {/* Sección: Órdenes Pendientes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #f59e0b', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#b45309', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PendingActionsIcon sx={{ fontSize: 24 }} />
            Órdenes Pendientes
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {ordenesPendientes.length === 0 ? (
                <Typography color="text.secondary">No hay órdenes pendientes.</Typography>
              ) : (
                ordenesPendientes.map((orden) => (
                  <Card key={orden.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>#{orden.id} · {orden.vehiculo}</Typography>
                      {renderEstado(orden.estado)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{orden.cliente}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {new Intl.DateTimeFormat('es-CO', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }).format(new Date(`${orden.fecha_recepcion}T12:00:00`))}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button variant="outlined" size="small" onClick={() => navigate(`/ordenes/${orden.id}`)}>
                        Ver Detalles
                      </Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => confirmarEliminacion(orden)}>
                        Eliminar
                      </Button>
                      <Button variant="contained" size="small" color="secondary" onClick={() => navigate(`/ordenes/${orden.id}/reparaciones?from=ordenes`)}>
                        Agregar Reparación
                      </Button>
                    </Box>
                  </Card>
                ))
              )}
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Placa</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Fecha de Recepción</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordenesPendientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No hay órdenes pendientes.</TableCell>
                  </TableRow>
                ) : (
                  ordenesPendientes.map((orden) => (
                    <TableRow key={orden.id}>
                      <TableCell>{orden.id}</TableCell>
                      <TableCell>{orden.vehiculo}</TableCell>
                      <TableCell>{orden.cliente}</TableCell>
                      <TableCell>
                        {new Intl.DateTimeFormat('es-CO', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }).format(new Date(`${orden.fecha_recepcion}T12:00:00`))}
                      </TableCell>
                      <TableCell>{renderEstado(orden.estado)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/ordenes/${orden.id}`)}
                        >
                          Ver Detalles
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => confirmarEliminacion(orden)}
                          sx={{ ml: 1 }}
                        >
                          Eliminar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="secondary"
                          onClick={() => navigate(`/ordenes/${orden.id}/reparaciones?from=ordenes`)}
                          sx={{ ml: 1 }}
                        >
                          Agregar Reparación
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      </motion.div>

      {/* Sección: Órdenes en Proceso */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #3b82f6', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#1d4ed8', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon sx={{ fontSize: 24 }} />
            Órdenes en Proceso
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {ordenesEnProceso.length === 0 ? (
                <Typography color="text.secondary">No hay órdenes en proceso.</Typography>
              ) : (
                ordenesEnProceso.map((orden) => (
                  <Card key={orden.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>#{orden.id} · {orden.vehiculo}</Typography>
                      {renderEstado(orden.estado)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{orden.cliente}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {new Date(orden.fecha_recepcion).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button variant="outlined" size="small" onClick={() => navigate(`/ordenes/${orden.id}`)}>
                        Ver Detalles
                      </Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => confirmarEliminacion(orden)}>
                        Eliminar
                      </Button>
                      <Button variant="contained" size="small" color="secondary" onClick={() => navigate(`/ordenes/${orden.id}/reparaciones?from=ordenes`)}>
                        Agregar Reparación
                      </Button>
                    </Box>
                  </Card>
                ))
              )}
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Placa</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Fecha de Recepción</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordenesEnProceso.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No hay órdenes en proceso.</TableCell>
                  </TableRow>
                ) : (
                  ordenesEnProceso.map((orden) => (
                    <TableRow key={orden.id}>
                      <TableCell>{orden.id}</TableCell>
                      <TableCell>{orden.vehiculo}</TableCell>
                      <TableCell>{orden.cliente}</TableCell>
                      <TableCell>{new Date(orden.fecha_recepcion).toLocaleDateString()}</TableCell>
                      <TableCell>{renderEstado(orden.estado)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/ordenes/${orden.id}`)}
                        >
                          Ver Detalles
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => confirmarEliminacion(orden)}
                          sx={{ ml: 1 }}
                        >
                          Eliminar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="secondary"
                          onClick={() => navigate(`/ordenes/${orden.id}/reparaciones?from=ordenes`)}
                          sx={{ ml: 1 }}
                        >
                          Agregar Reparación
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      </motion.div>

      {/* Sección: Historial de Órdenes Cerradas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.35, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: '4px solid #083a94', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#083a94', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ fontSize: 24 }} />
            Historial de Órdenes Cerradas
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filtroDesde}
              onChange={e => setFiltroDesde(e.target.value)}
              size="small"
            />
            <TextField
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filtroHasta}
              onChange={e => setFiltroHasta(e.target.value)}
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Search />}
              onClick={buscarCerradas}
            >
              Buscar
            </Button>
          </Box>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {cerradasLoading ? (
                <Typography color="text.secondary">Buscando...</Typography>
              ) : ordenesCerradas.length === 0 ? (
                <Typography color="text.secondary">No hay órdenes cerradas para el rango seleccionado.</Typography>
              ) : (
                ordenesCerradas.map((orden) => (
                  <Card key={orden.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>#{orden.id} · {orden.vehiculo}</Typography>
                      {renderEstado(orden.estado)}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{orden.cliente}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {new Date(orden.fecha_recepcion).toLocaleDateString()}
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => navigate(`/ordenes/${orden.id}`)}>
                      Ver Detalles
                    </Button>
                  </Card>
                ))
              )}
            </Box>
          ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Placa</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Fecha de Recepción</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cerradasLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Buscando...</TableCell>
                  </TableRow>
                ) : ordenesCerradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No hay órdenes cerradas para el rango seleccionado.</TableCell>
                  </TableRow>
                ) : (
                  ordenesCerradas.map((orden) => (
                    <TableRow key={orden.id}>
                      <TableCell>{orden.id}</TableCell>
                      <TableCell>{orden.vehiculo}</TableCell>
                      <TableCell>{orden.cliente}</TableCell>
                      <TableCell>{new Date(orden.fecha_recepcion).toLocaleDateString()}</TableCell>
                      <TableCell>{renderEstado(orden.estado)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/ordenes/${orden.id}`)}
                        >
                          Ver Detalles
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      </motion.div>

      {/* Dialogo de confirmación de eliminación */}
      <Dialog open={openDialog} onClose={cancelarEliminacion}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar la orden #{ordenAEliminar?.id}? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarEliminacion}>Cancelar</Button>
          <Button onClick={eliminarOrden} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OrdenesDashboard;
